export default function PracticeScreen({
  topic,
  gradeInfo,
  currentProblem,
  currentProblemIndex,
  problems,
  answer,
  setAnswer,
  feedback,
  score,
  attempts,
  showHint,
  setShowHint,
  streak,
  xp,
  onCheckAnswer,
  onNextProblem,
  onBack,
  inputRef,
  chatHandlers,
}) {
  const { setIsChatOpen } = chatHandlers;

  return (
    <div style={{ position: "relative", zIndex: 1, padding: "20px", maxWidth: "760px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <button onClick={onBack} style={{
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "10px",
          color: "white",
          padding: "6px 14px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 700,
        }}>
          ← Back
        </button>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {streak >= 2 && (
            <div style={{
              background: "rgba(255, 150, 50, 0.2)",
              border: "1px solid rgba(255, 150, 50, 0.4)",
              borderRadius: "20px",
              padding: "4px 12px",
              color: "#FFB347",
              fontSize: "13px",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}>
              🔥 {streak} streak!
            </div>
          )}
          <div style={{
            background: "rgba(255, 230, 109, 0.15)",
            borderRadius: "20px",
            padding: "4px 14px",
            color: "#FFE66D",
            fontSize: "13px",
            fontWeight: 800,
          }}>
            ⭐ {xp} XP
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ color: "#a0b4ff", fontSize: "13px", fontWeight: 700 }}>
            {topic?.emoji} {topic?.name}
          </span>
          <span style={{ color: "#a0b4ff", fontSize: "13px" }}>
            {currentProblemIndex + 1} / {problems.length}
          </span>
        </div>
        <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: "10px", height: "8px", overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${(currentProblemIndex / problems.length) * 100}%`,
            background: `linear-gradient(90deg, ${gradeInfo?.color || "#6C63FF"}, ${gradeInfo?.color || "#6C63FF"}aa)`,
            borderRadius: "10px",
            transition: "width 0.5s ease",
          }} />
        </div>
      </div>

      {/* Problem Card */}
      <div className="slide-up" style={{
        background: "rgba(255,255,255,0.06)",
        border: `2px solid ${feedback === "correct" ? "#4ECDC4" : feedback === "wrong" ? "#FF6B6B" : "rgba(255,255,255,0.12)"}`,
        borderRadius: "24px",
        padding: "32px",
        marginBottom: "16px",
        backdropFilter: "blur(15px)",
        transition: "border-color 0.3s ease",
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${gradeInfo?.color || "#6C63FF"}33, transparent)`,
          borderRadius: "16px",
          padding: "20px 24px",
          marginBottom: "24px",
        }}>
          <p style={{
            color: "white",
            fontSize: "clamp(18px, 3vw, 24px)",
            fontWeight: 700,
            margin: 0,
            lineHeight: 1.5,
          }}>
            {currentProblem.q}
          </p>
        </div>

        {/* Answer input */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
          <input
            ref={inputRef}
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") feedback ? onNextProblem() : onCheckAnswer(); }}
            disabled={!!feedback}
            placeholder="Type your answer..."
            className={feedback === "wrong" ? "shake" : ""}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.08)",
              border: "2px solid rgba(255,255,255,0.15)",
              borderRadius: "14px",
              padding: "14px 20px",
              color: "white",
              fontSize: "18px",
              fontFamily: "'Nunito', sans-serif",
              fontWeight: 700,
              outline: "none",
              transition: "border-color 0.2s",
            }}
          />
          {!feedback ? (
            <button
              className="btn-primary"
              onClick={onCheckAnswer}
              style={{
                background: gradeInfo?.color || "#6C63FF",
                border: "none",
                borderRadius: "14px",
                padding: "14px 28px",
                color: "white",
                fontSize: "16px",
                fontWeight: 800,
                cursor: "pointer",
                transition: "transform 0.15s ease",
                whiteSpace: "nowrap",
              }}>
              Check ✓
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={onNextProblem}
              style={{
                background: feedback === "correct" ? "#4ECDC4" : "#6C63FF",
                border: "none",
                borderRadius: "14px",
                padding: "14px 24px",
                color: "white",
                fontSize: "16px",
                fontWeight: 800,
                cursor: "pointer",
                transition: "transform 0.15s ease",
                whiteSpace: "nowrap",
              }}>
              Next →
            </button>
          )}
        </div>

        {/* Feedback */}
        {feedback === "correct" && (
          <div className="pop-in" style={{
            background: "rgba(78, 205, 196, 0.15)",
            border: "1px solid rgba(78, 205, 196, 0.4)",
            borderRadius: "12px",
            padding: "12px 18px",
            color: "#4ECDC4",
            fontWeight: 800,
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <span style={{ fontSize: "24px" }}>🎉</span>
            {streak >= 3 ? "AMAZING! You're on fire! 🔥" : streak >= 2 ? "Excellent work! Keep it up! ⭐" : "Correct! Well done!"}
          </div>
        )}
        {feedback === "wrong" && (
          <div className="pop-in" style={{
            background: "rgba(255, 107, 107, 0.12)",
            border: "1px solid rgba(255, 107, 107, 0.3)",
            borderRadius: "12px",
            padding: "12px 18px",
            color: "#FF8B8B",
            fontWeight: 700,
            fontSize: "15px",
          }}>
            <div style={{ marginBottom: "4px" }}>❌ Not quite! The answer is <strong style={{ color: "#FF6B6B" }}>{currentProblem.a}</strong></div>
            <div style={{ fontSize: "13px", color: "#c09090" }}>Don't worry — mistakes help you learn! 💪</div>
          </div>
        )}

        {/* Hint */}
        <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
          <button
            onClick={() => setShowHint(!showHint)}
            style={{
              background: "rgba(255, 230, 109, 0.1)",
              border: "1px solid rgba(255, 230, 109, 0.3)",
              borderRadius: "10px",
              padding: "8px 16px",
              color: "#FFE66D",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
            }}>
            💡 {showHint ? "Hide" : "Show"} Hint
          </button>
          <button
            onClick={() => setIsChatOpen(true)}
            style={{
              background: "rgba(108, 99, 255, 0.15)",
              border: "1px solid rgba(108, 99, 255, 0.35)",
              borderRadius: "10px",
              padding: "8px 16px",
              color: "#a09bff",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
            }}>
            🤖 Ask AI Tutor
          </button>
        </div>

        {showHint && (
          <div className="slide-up" style={{
            marginTop: "12px",
            background: "rgba(255, 230, 109, 0.08)",
            border: "1px solid rgba(255, 230, 109, 0.2)",
            borderRadius: "12px",
            padding: "12px 16px",
            color: "#FFE66D",
            fontSize: "14px",
            fontWeight: 600,
          }}>
            💡 {currentProblem.hint}
          </div>
        )}
      </div>

      {/* Score indicator */}
      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
        {problems.map((_, idx) => (
          <div key={idx} style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: idx < currentProblemIndex
              ? (attempts[idx] ? "#FF8B8B" : "#4ECDC4")
              : idx === currentProblemIndex
              ? gradeInfo?.color || "#6C63FF"
              : "rgba(255,255,255,0.2)",
            transition: "background 0.3s",
          }} />
        ))}
      </div>
    </div>
  );
}
